import {
  Injectable,
  PLATFORM_ID,
  computed,
  inject,
  signal,
} from '@angular/core';
import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root',
})
export class SessionTimeoutService {
  private readonly idleTimeoutMs = 5 * 60 * 1000;
  private readonly warningTimeoutMs = 2 * 60 * 1000;
  private readonly syncEventKey = 'rma_session_sync_event';
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly authService = inject(AuthService);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly activityEvents = [
    'click',
    'keydown',
    'mousemove',
    'mousedown',
    'scroll',
    'touchstart',
  ] as const;

  private initialized = false;
  private listening = false;
  private idleTimer: number | null = null;
  private warningTimer: number | null = null;
  private countdownTimer: number | null = null;
  private storageListening = false;
  private readonly activityHandler = () => {
    if (this.warningVisible()) {
      return;
    }

    this.resetIdleTimer();
  };
  private readonly storageHandler = (event: StorageEvent) => {
    if (
      event.key !== this.syncEventKey ||
      !event.newValue ||
      event.storageArea !== this.document.defaultView?.localStorage
    ) {
      return;
    }

    try {
      const payload = JSON.parse(event.newValue) as {
        type?: 'extended' | 'timed-out';
        redirectTo?: string;
      };

      if (payload.type === 'extended') {
        this.handleRemoteSessionExtended();
      }

      if (payload.type === 'timed-out') {
        this.handleRemoteSessionTimedOut(payload.redirectTo || '/login');
      }
    } catch {
      // Ignore malformed sync payloads.
    }
  };

  readonly warningVisible = signal(false);
  readonly countdownSeconds = signal(this.warningTimeoutMs / 1000);
  readonly isExtendingSession = signal(false);
  readonly formattedCountdown = computed(() => {
    const totalSeconds = this.countdownSeconds();
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  });

  init(): void {
    if (!this.isBrowser || this.initialized) {
      return;
    }

    this.initialized = true;
    this.attachStorageListener();

    this.authService.currentUser$.subscribe(user => {
      if (user && this.authService.getToken()) {
        this.startMonitoring();
        return;
      }

      this.stopMonitoring();
    });
  }

  extendSession(): void {
    if (!this.isBrowser) {
      return;
    }

    this.isExtendingSession.set(true);
    this.authService.refreshSession().subscribe({
      next: () => {
        this.isExtendingSession.set(false);
        this.resumeSessionLocally();
        this.broadcastSyncEvent('extended');
      },
      error: () => {
        this.isExtendingSession.set(false);
        this.expireSession(true);
      },
    });
  }

  logoutNow(): void {
    this.expireSession(false);
  }

  private startMonitoring(): void {
    if (!this.isBrowser) {
      return;
    }

    if (!this.listening) {
      this.attachActivityListeners();
      this.listening = true;
    }

    this.warningVisible.set(false);
    this.countdownSeconds.set(this.warningTimeoutMs / 1000);
    this.clearWarningTimers();
    this.resetIdleTimer();
  }

  private stopMonitoring(): void {
    this.warningVisible.set(false);
    this.countdownSeconds.set(this.warningTimeoutMs / 1000);
    this.clearIdleTimer();
    this.clearWarningTimers();

    if (!this.listening || !this.isBrowser) {
      return;
    }

    this.detachActivityListeners();
    this.listening = false;
  }

  private resetIdleTimer(): void {
    const browserWindow = this.document.defaultView;
    if (!browserWindow) {
      return;
    }

    this.clearIdleTimer();
    this.idleTimer = browserWindow.setTimeout(() => {
      this.showWarning();
    }, this.idleTimeoutMs);
  }

  private showWarning(): void {
    const browserWindow = this.document.defaultView;
    if (!this.authService.getCurrentUser() || !this.authService.getToken()) {
      this.stopMonitoring();
      return;
    }

    if (!browserWindow) {
      this.stopMonitoring();
      return;
    }

    this.warningVisible.set(true);
    this.countdownSeconds.set(this.warningTimeoutMs / 1000);
    this.clearIdleTimer();
    this.clearWarningTimers();

    this.countdownTimer = browserWindow.setInterval(() => {
      const nextValue = this.countdownSeconds() - 1;
      this.countdownSeconds.set(Math.max(nextValue, 0));
    }, 1000);

    this.warningTimer = browserWindow.setTimeout(() => {
      this.expireSession(true);
    }, this.warningTimeoutMs);
  }

  private expireSession(timedOut: boolean): void {
    const redirectTo = this.resolveLoginRoute();
    this.stopMonitoring();
    this.isExtendingSession.set(false);

    if (timedOut) {
      this.broadcastSyncEvent('timed-out', redirectTo);
    }

    this.authService.logout(
      timedOut
        ? {
            redirectTo,
            navigationExtras: {
              queryParams: {
                session: 'timed-out',
              },
              replaceUrl: true,
            },
          }
        : {
            redirectTo,
          }
    );
  }

  private resolveLoginRoute(): string {
    const role = (this.authService.getCurrentUser()?.role ?? '').toLowerCase();

    if (role === 'support') {
      return '/support/login';
    }

    if (role === 'admin') {
      return '/admin/login';
    }

    if (role === 'super_admin') {
      return '/super-admin/login';
    }

    return '/login';
  }

  private attachActivityListeners(): void {
    const browserWindow = this.document.defaultView;
    if (!browserWindow) {
      return;
    }

    this.activityEvents.forEach(eventName => {
      browserWindow.addEventListener(eventName, this.activityHandler, {
        passive: true,
      });
    });
  }

  private detachActivityListeners(): void {
    const browserWindow = this.document.defaultView;
    if (!browserWindow) {
      return;
    }

    this.activityEvents.forEach(eventName => {
      browserWindow.removeEventListener(eventName, this.activityHandler);
    });
  }

  private clearIdleTimer(): void {
    const browserWindow = this.document.defaultView;
    if (this.idleTimer) {
      browserWindow?.clearTimeout(this.idleTimer);
      this.idleTimer = null;
    }
  }

  private clearWarningTimers(): void {
    const browserWindow = this.document.defaultView;
    if (this.warningTimer) {
      browserWindow?.clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }

    if (this.countdownTimer) {
      browserWindow?.clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  }

  private resumeSessionLocally(): void {
    this.warningVisible.set(false);
    this.countdownSeconds.set(this.warningTimeoutMs / 1000);
    this.clearWarningTimers();
    this.resetIdleTimer();
  }

  private attachStorageListener(): void {
    const browserWindow = this.document.defaultView;
    if (!browserWindow || this.storageListening) {
      return;
    }

    browserWindow.addEventListener('storage', this.storageHandler);
    this.storageListening = true;
  }

  private broadcastSyncEvent(
    type: 'extended' | 'timed-out',
    redirectTo?: string
  ): void {
    const browserWindow = this.document.defaultView;
    if (!browserWindow?.localStorage) {
      return;
    }

    browserWindow.localStorage.setItem(
      this.syncEventKey,
      JSON.stringify({
        type,
        redirectTo: redirectTo ?? null,
        timestamp: Date.now(),
      })
    );
  }

  private handleRemoteSessionExtended(): void {
    if (!this.authService.getToken() || !this.authService.getCurrentUser()) {
      return;
    }

    this.startMonitoring();
  }

  private handleRemoteSessionTimedOut(redirectTo: string): void {
    this.stopMonitoring();
    this.isExtendingSession.set(false);
    this.authService.logout({
      redirectTo,
      navigationExtras: {
        queryParams: {
          session: 'timed-out',
        },
        replaceUrl: true,
      },
    });
  }
}
